import axios from 'axios';
import {
  clearAuthStorage,
  dispatchSessionExpired,
  AUTH_STORAGE_KEYS,
  getStoredAccessToken,
  getStoredRefreshToken,
  hasStoredAuthSession,
  isIdleSessionExpired
} from '../utils/authSession';


const DEFAULT_API_BASE_URL = 'https://api.sanspath.co.uk/api/v1';

export const API_BASE_URL = String(process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL)
  .trim()
  .replace(/\/+$/, '');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

let refreshTokenRequest = null;

export const createClientRequestId = (prefix = 'admin-web') => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
};

const getConfigHeader = (headers, name) => {
  if (!headers) return null;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] || headers[name.toLowerCase()] || null;
};

const setConfigHeader = (headers, name, value) => {
  if (!headers) return;
  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }
  headers[name] = value;
};

const adminAuthEndpoints = [
  '/admin/auth/login',
  '/admin/auth/login/verify-email',
  '/admin/auth/login/resend-email-verification',
  '/admin/auth/refresh-token',
  '/admin/auth/logout'
];

const shouldSkipRefresh = (url = '') => adminAuthEndpoints.some((endpoint) => String(url).includes(endpoint));

const refreshAccessToken = async () => {
  if (refreshTokenRequest) return refreshTokenRequest;

  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) throw new Error('No refresh token available');

  refreshTokenRequest = axios.post(`${API_BASE_URL}/admin/auth/refresh-token`, { refreshToken }, {
    headers: { 'Content-Type': 'application/json' }
  }).then((response) => {
    const data = response.data || {};
    const token = data.token || data.accessToken;
    const nextRefreshToken = data.refreshToken || refreshToken;

    if (!token) {
      throw new Error('No access token returned from refresh');
    }

    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, token);
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, nextRefreshToken);

    try {
      const { default: useAuthStore } = require('../store/authStore');
      useAuthStore.getState().setToken(token, nextRefreshToken);
      if (data.user) useAuthStore.getState().updateUser(data.user);
    } catch (_error) {
      // Store may not be ready during very early boot.
    }

    return { token, refreshToken: nextRefreshToken };
  }).finally(() => {
    refreshTokenRequest = null;
  });

  return refreshTokenRequest;
};

const clearClientAuthSession = (reason = 'auth_failed') => {
  try {
    const { default: useAuthStore } = require('../store/authStore');
    useAuthStore.getState().logout({ callApi: false, reason });
  } catch (_error) {
    clearAuthStorage();
  }
  dispatchSessionExpired(reason);
};

apiClient.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: Date.now(),
    requestId: getConfigHeader(config.headers, 'X-Request-Id') || createClientRequestId()
  };
  setConfigHeader(config.headers, 'X-Request-Id', config.metadata.requestId);

  if (!config.skipIdleCheck && hasStoredAuthSession() && isIdleSessionExpired()) {
    clearClientAuthSession('idle_timeout');
    return Promise.reject({
      message: 'Session expired after 30 minutes of inactivity. Please log in again.',
      code: 'SESSION_EXPIRED'
    });
  }

  const token = getStoredAccessToken();
  if (!config.skipAuthHeader && token) {
    setConfigHeader(config.headers, 'Authorization', `Bearer ${token}`);
  }

  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const original = error.config;
    const shouldAttemptRefresh = error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.skipAuthRefresh &&
      !shouldSkipRefresh(original.url);

    if (shouldAttemptRefresh) {
      original._retry = true;

      try {
        if (hasStoredAuthSession() && isIdleSessionExpired()) {
          throw new Error('Session expired due to inactivity');
        }

        const refreshed = await refreshAccessToken();
        setConfigHeader(original.headers, 'Authorization', `Bearer ${refreshed.token}`);
        return apiClient(original);
      } catch (refreshError) {
        clearClientAuthSession(
          refreshError?.message?.includes('inactivity') ? 'idle_timeout' : 'auth_failed'
        );

        return Promise.reject({
          message: refreshError?.message?.includes('inactivity')
            ? 'Session expired after 30 minutes of inactivity. Please log in again.'
            : 'Authentication failed. Please log in again.',
          code: refreshError?.message?.includes('inactivity') ? 'SESSION_EXPIRED' : 'AUTH_FAILED'
        });
      }
    }

    if (!error.response) {
      return Promise.reject({
        message: 'Unable to connect to the server.',
        code: 'NETWORK_ERROR'
      });
    }

    return Promise.reject({
      message: error.response.data?.message || error.response.data?.error || error.message || 'Request failed',
      status: error.response.status,
      response: error.response.data
    });
  }
);

export default apiClient;
